/**
 * CMMC scorecard — V102-8-2 (ADR-0058; ux-adversary run cmmc-self-assessment,
 * round 7 `program-cmmc.web.html`; PR A critic panels r1 + r2). The CMMC
 * Prepare tab, in the `ReadinessHero` pattern. Hero order, every state and
 * width: score → verdict → deadline line → meter → primary.
 *
 *   hero      the score at display size (or the Level 1 result), "▲ vs last
 *             posted" only when a prior posting exists, the verdict line with
 *             its mark and Provisional tag, ONE deadline line (the POA&M
 *             closeout while under 30 days, else the nearest customer
 *             deadline — plain toned text; a lead / admin with none gets a
 *             quiet "+ Add a customer deadline" that opens the form below the
 *             actions), the meter with the 88 tick, ONE primary.
 *   details   Verify and Customer deadlines share one quiet row.
 *   blockers  ONE numbered list; plain rows (title first, code dim), one
 *             action per blocker.
 *   gate      the affirmation as a locked card listing only what the list
 *             doesn't: results awaiting review (inline, with the cited
 *             evidence and Mark reviewed) and starting fieldwork at Audit.
 *
 * One polite status region announces saves, archives and reviews. Arriving
 * with `#<anchor>` (Today's links) focuses that heading / row.
 *
 * Reads (any member; session workspace): score, objective readiness, POA&M,
 * affirmation gate, affirmations, customer deadlines, proposed
 * determinations. Writes (program lead or owner/admin, server-enforced; the
 * controls are only hidden otherwise): Mark reviewed (reviewer ≠ proposer,
 * 403 `sod`), customer deadlines (add / edit / archive).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Spinner } from '@aigovops/design-system';
import type { ResolvedEvidenceRef } from '@aigovops/core';
import { ApiError, createCustomerRequirement, listDeterminations, reviewDetermination, startProgramFieldwork, type CustomerRequirement, type ProgramCard } from '../../lib/api';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { buildScorecard, fmtShort, reqRef, type Action, type Blocker, type GateView, type ScorecardModel } from '../../lib/cmmc-model';
import { useCmmcData } from '../../lib/use-cmmc';
import { fmtProgramDate } from '../../lib/program-shell';
import { ActionDialog, Chip } from '../today/shared';
import { AskAdmin } from '../AskAdmin';
import { CustomerDeadlineForm, CustomerDeadlineList, dismissedKey } from './CmmcCustomerDeadlines';
import { SendBackForm } from './CmmcRecord';
import { focusAfterSettle } from '../../lib/focus-settle';
import '../../pages/programs.css';
import '../../pages/cert-ops.css';
import '../../pages/today.css';
import '../../pages/cmmc.css';

export { useCmmcData };

export interface CmmcScorecardProps {
  slug: string;
  prefix: string;
  card: ProgramCard;
  periodId: string;
  periodLabel: string;
  tabHref: (t: 'prepare' | 'audit' | 'report') => string;
  isAdmin: boolean;
  /** Program lead or owner/admin (server-enforced on every write). */
  canManage?: boolean;
  viewerId?: string;
  names?: ReadonlyMap<string, string>;
}

/** The model for one period, from the shared reads. */
export function useScorecardModel(slug: string, prefix: string, card: ProgramCard, periodId: string, tabHref: CmmcScorecardProps['tabHref'], titles = false) {
  const d = useCmmcData(slug, card.program, periodId, { titles });
  const model = useMemo(() => {
    if (!d.score.data) return null;
    const t = new Map((d.readiness.data?.readiness.requirements ?? []).map((r) => [r.requirement_id, r.title] as const));
    return buildScorecard({
      score: d.score.data.score, gate: d.gate.data?.gate ?? null, titles: t, affirmations: d.affirmations.data?.affirmations, poam: d.poam.data?.poam,
      deadlines: d.customers.data?.deadlines,
      todayDay: new Date().toISOString().slice(0, 10), prefix, program: card.program, periodId, tabHref,
    });
  }, [d.score.data, d.gate.data, d.readiness.data, d.affirmations.data, d.poam.data, d.customers.data, prefix, card.program, periodId, tabHref]);
  return { d, model };
}

const focusId = (id: string) => {
  const el = document.getElementById(id);
  el?.scrollIntoView?.({ block: 'start' });
  el?.focus();
};

/** A per-viewer convenience (not data): the lead said this period has no customer deadline. */
function useDismissed(key: string): [boolean, () => void, () => void] {
  const read = () => { try { return localStorage.getItem(key) === '1'; } catch { return false; } };
  const [v, setV] = useState(read);
  const set = () => { try { localStorage.setItem(key, '1'); } catch { /* storage unavailable: dismissed for this view only */ } setV(true); };
  const clear = () => { try { localStorage.removeItem(key); } catch { /* storage unavailable */ } setV(false); };
  return [v, set, clear];
}

export function CmmcScorecard({ slug, prefix, card, periodId, periodLabel, tabHref, isAdmin, canManage = isAdmin, viewerId = '', names }: CmmcScorecardProps) {
  const { d, model: m } = useScorecardModel(slug, prefix, card, periodId, tabHref, true);
  const titles = useMemo(() => new Map((d.readiness.data?.readiness.requirements ?? []).map((r) => [r.requirement_id, r.title] as const)), [d.readiness.data]);
  const { hash } = useLocation();
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const announce = useCallback((s: string) => setStatus(s), []);
  const [adding, setAdding] = useState(false);
  const [dismissed, dismiss, undismiss] = useDismissed(dismissedKey(slug, periodId, viewerId));
  // a11y T8R3-3/4: the archived row's Undo lives here so it outlives the list (cleared by the next action or navigation).
  const [undo, setUndo] = useState<CustomerRequirement | null>(null);
  // Focus that must wait for a re-render (the trigger / line / row appears after a refetch or a state change).
  const pendingFocus = useRef<string | null>(null);
  const focusLater = (id: string) => { pendingFocus.current = id; };
  useEffect(() => {
    if (!pendingFocus.current) return;
    const el = document.getElementById(pendingFocus.current);
    if (el) { el.focus(); pendingFocus.current = null; }
  });

  // Today's "Close …" links land on #<anchor>: focus that heading / row once the model renders.
  useEffect(() => {
    if (!m || !hash || hash.length < 2) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!el) return;
    el.scrollIntoView?.({ block: 'start' });
    el.focus();
  }, [m, hash]);

  if (d.score.isLoading) return <section className="gp-hero" aria-label="CMMC score"><Spinner label="Loading the score" /></section>;
  if (d.score.error || !d.score.data || !m) {
    const code = d.score.error instanceof ApiError ? (d.score.error.body as { code?: string } | undefined)?.code : undefined;
    if (code === 'rules_not_approved') {
      return <EmptyState title="The CMMC scoring rules are not approved yet"
        action={isAdmin ? <Link className="gp-linkbtn" to={`${prefix}/queue`}>Review them in the content queue</Link> : <Link className="gp-linkbtn" to={`${prefix}/settings/team`}>See who can approve them</Link>} />;
    }
    return <section className="gp-hero" aria-label="CMMC score"><Alert variant="error"><p>The score could not load.</p><Button onClick={() => void d.score.refetch()}>Retry</Button></Alert></section>;
  }
  const rs = d.score.data.score.rule_set;
  const requirements = d.customers.data?.requirements ?? [];
  const live = requirements.filter((r) => !r.archived_at);
  const offerAdd = canManage && d.customers.isSuccess && !live.length;
  const refresh = () => Promise.all([['customer-requirements', slug] as const, ...invalidationMap['program:changed'](slug)].map((queryKey) => qc.invalidateQueries({ queryKey })));

  const onArchived = (r: CustomerRequirement, wasLast: boolean) => {
    setUndo(r);
    announce(`${r.customer_name} archived · Undo available`);
    if (wasLast) focusLater('cmmc-add-trigger');
  };
  // There is no unarchive: Undo re-creates the row with the same fields.
  const restore = async () => {
    const r = undo;
    if (!r) return;
    setUndo(null);
    try {
      await createCustomerRequirement(slug, card.program, { customer_name: r.customer_name, required_level: r.required_level, required_status: r.required_status, required_by: r.required_by, contract_ref: r.contract_ref, note: r.note });
      await refresh();
      announce(`${r.customer_name} restored`);
      focusLater('cmmc-deadline-line');
    } catch (e) { announce(e instanceof Error ? e.message : 'The deadline could not be restored.'); }
  };
  const openAdd = () => { setUndo(null); undismiss(); setAdding(true); };

  const undoLine = undo ? (
    <p className="gp-cmmc-note" data-part="undo">{`Archived ${undo.customer_name} · `}<button type="button" className="gp-cmmc-quiet" onClick={() => void restore()}>Undo</button></p>
  ) : null;
  const slot = m.closeout ? (
    <div data-part="closeout">
      <p className="gp-cmmc-toned" data-tone={m.closeout.tone} id="cmmc-deadline-line" tabIndex={-1}>{m.closeout.text}</p>
      {m.closeout.note ? <p className="gp-cmmc-note">{m.closeout.note}</p> : null}
    </div>
  ) : m.customer ? (
    <p className="gp-cmmc-toned" data-part="customer-deadline" data-tone={m.customer.tone} id="cmmc-deadline-line" tabIndex={-1}>{m.customer.text}</p>
  ) : offerAdd && dismissed ? (
    // R3: the dismissal stays visible and reversible.
    <p className="gp-cmmc-note" data-part="no-deadline">
      {'No customer deadline · '}
      <button type="button" id="cmmc-add-trigger" className="gp-cmmc-quiet" onClick={openAdd}>Add one</button>
      {' · '}
      <button type="button" className="gp-cmmc-quiet" onClick={() => { undismiss(); focusLater('cmmc-add-trigger'); announce('Customer deadline prompt restored'); }}>Undo</button>
    </p>
  ) : offerAdd && !adding ? (
    <p className="gp-cmmc-deadline-line"><button type="button" id="cmmc-add-trigger" className="gp-cmmc-quiet" data-part="add-deadline" onClick={openAdd}>+ Add a customer deadline</button></p>
  ) : null;
  const slotWithUndo = <>{slot}{undoLine}</>;

  const addForm = offerAdd && adding && !dismissed ? (
    <section className="gp-cmmc-addform" aria-labelledby="cmmc-add-h" data-part="add-deadline-form">
      <h4 id="cmmc-add-h">Which customer needs this, by when?</h4>
      <CustomerDeadlineForm slug={slug} program={card.program} level={m.level} submitLabel="Save deadline" autoFocus announce={announce}
        onCancel={() => { setAdding(false); focusLater('cmmc-add-trigger'); }}
        onDone={() => { setAdding(false); setUndo(null); focusLater('cmmc-deadline-line'); }}
        extra={<Button variant="ghost" onClick={() => { dismiss(); setAdding(false); announce('No customer deadline for this period'); focusLater('cmmc-add-trigger'); }}>No customer deadline</Button>} />
    </section>
  ) : null;

  return (
    <div className="gp-tab-panel" data-part="cmmc-scorecard" data-level={m.level}>
      <p role="status" aria-live="polite" className="sr-only" data-part="cmmc-status">{status}</p>
      {m.l2 ? <Level2Hero m={m} periodLabel={periodLabel} slot={slotWithUndo} after={addForm} /> : <Level1Hero m={m} periodLabel={periodLabel} slot={slotWithUndo} after={addForm} />}

      <div className="gp-cmmc-disclosures">
        <details className="gp-cmmc-disclosure" data-part="verify">
          <summary>Verify</summary>
          <dl className="gp-cmmc-verify">
            <dt>Rule set</dt><dd>{`${rs.rule_set_id} · version ${rs.version} · ${rs.basis === 'pinned' ? 'pinned at Audit' : 'current (not pinned)'}`}</dd>
            <dt>Rules hash</dt><dd><code>{rs.content_sha256}</code></dd>
            <dt>Method</dt><dd>{m.l2 ? `${m.l2.max} minus the points of each unmet requirement · not assessed counts as unmet` : 'Every Level 1 requirement Met · each rolls up its Level 2 rows'}</dd>
          </dl>
        </details>
        {canManage && live.length
          ? <CustomerDeadlineList slug={slug} program={card.program} level={m.level} requirements={requirements} announce={announce}
              onArchived={onArchived} onAction={() => setUndo(null)} />
          : null}
      </div>

      <BlockerList m={m} />

      {m.gate ? (
        <GateCard gate={m.gate}
          renderStep={(step) => step === 'awaiting_review'
            ? <ReviewList slug={slug} prefix={prefix} program={card.program} periodId={periodId} canManage={canManage} viewerId={viewerId} names={names} titles={titles} announce={announce} />
            : null} />
      ) : d.gate.isLoading ? <Spinner label="Loading the affirmation gate" /> : null}
    </div>
  );
}

/**
 * Every hero action is the same element and size (a link-button): anchors jump
 * in-page and move focus; hrefs navigate.
 */
function ActionButton({ a, primary }: { a: Action; primary: boolean }) {
  const part = primary ? 'primary' : 'secondary';
  const cls = `gp-linkbtn${primary ? ' btn-primary' : ''}`;
  if (a.href) return <Link className={cls} to={a.href} data-part={part}>{a.label}</Link>;
  return <a className={cls} href={`#${a.anchor}`} data-part={part} onClick={(e) => { e.preventDefault(); focusId(a.anchor!); }}>{a.label}</a>;
}

function PrimaryActions({ m }: { m: ScorecardModel }) {
  if (!m.primary) return null;
  return (
    <div className="gp-actions" data-part="cmmc-actions">
      <ActionButton a={m.primary} primary />
      {m.secondary ? <ActionButton a={m.secondary} primary={false} /> : null}
    </div>
  );
}

/** The verdict line: a mark (red square / green check) + bold words, in the verdict's tone; Provisional is the only chip. */
function VerdictLine({ m }: { m: ScorecardModel }) {
  return (
    <p className="gp-cmmc-verdict" data-part="verdict" data-tone={m.verdict.tone}>
      <span className="gp-cmmc-mark" data-mark={m.verdict.mark} aria-hidden="true" />
      <strong>{m.verdict.text}</strong>
      {m.provisional ? <Chip text={m.provisional} tone="outline" /> : null}
    </p>
  );
}

function Level2Hero({ m, periodLabel, slot, after }: { m: ScorecardModel; periodLabel: string; slot: ReactNode; after: ReactNode }) {
  const s = m.l2!;
  const tick = Math.min(100, Math.max(0, (s.threshold / s.max) * 100));
  return (
    <section className="gp-hero" aria-labelledby="cmmc-score-h" data-part="cmmc-hero">
      <h3 id="cmmc-score-h" className="gp-hero-label">{`SPRS score · ${periodLabel}`}</h3>
      <p className="gp-cmmc-score" data-part="score"><b>{s.score}</b><span>{` pts / ${s.max}`}</span>
        {/* Glance H2: the delta only when a prior posted score exists (never invented). */}
        {s.delta ? <span className="gp-cmmc-delta" data-part="delta">{s.delta}</span> : null}</p>
      <VerdictLine m={m} />
      {slot}
      <div className="gp-cmmc-meter-wrap">
        <div className="gp-cmmc-meter" role="img" data-part="cmmc-meter"
          aria-label={`${s.earned} points earned, ${s.notAssessedPts} points not assessed, ${s.notMetPts} points not met, of ${s.max}; ${s.threshold} for conditional`}>
          {s.earned ? <span className="gp-cmmc-seg gp-cmmc-seg--earned" style={{ flexGrow: s.earned }} /> : null}
          {s.notAssessedPts ? <span className="gp-cmmc-seg gp-cmmc-seg--unassessed" style={{ flexGrow: s.notAssessedPts }} /> : null}
          {s.notMetPts ? <span className="gp-cmmc-seg gp-cmmc-seg--notmet" style={{ flexGrow: s.notMetPts }} /> : null}
        </div>
        <span className="gp-cmmc-tick" style={{ left: `${tick}%` }} aria-hidden="true"><span>{s.threshold}</span></span>
      </div>
      <p className="gp-meter-legend" aria-hidden="true">
        {s.earned ? <span><i className="gp-cmmc-seg--earned" />{`${s.earned} pts earned`}</span> : null}
        {s.notAssessedPts ? <span><i className="gp-cmmc-seg--unassessed" />{`${s.notAssessedPts} pts not assessed`}</span> : null}
        {s.notMetPts ? <span><i className="gp-cmmc-seg--notmet" />{`${s.notMetPts} pts not met`}</span> : null}
      </p>
      {s.notAssessedPts ? <p className="gp-cmmc-note" data-part="unassessed-note">Not assessed counts as unmet until recorded.</p> : null}
      <p className="gp-cmmc-gates" data-part="threshold">{s.thresholdLine}</p>
      {m.l1.line ? (
        <p className="gp-cmmc-l1line" data-part="l1-line">
          {m.l1.line.anchor
            ? <a href={`#${m.l1.line.anchor}`} onClick={(e) => { e.preventDefault(); focusId(m.l1.line!.anchor!); }}><Chip text={m.l1.line.text} tone={m.l1.line.tone} /></a>
            : <Chip text={m.l1.line.text} tone={m.l1.line.tone} />}
        </p>
      ) : null}
      <PrimaryActions m={m} />
      {after}
    </section>
  );
}

/** H3-14: the Level 1 result is red until met, with its lapse countdown (the date said once). */
function Level1Hero({ m, periodLabel, slot, after }: { m: ScorecardModel; periodLabel: string; slot: ReactNode; after: ReactNode }) {
  const l1 = m.l1;
  return (
    <section className="gp-hero" aria-labelledby="cmmc-l1-h" data-part="cmmc-hero">
      <h3 id="cmmc-l1-h" className="gp-hero-label">{`Level 1 result · ${periodLabel}`}</h3>
      <p className="gp-cmmc-score" data-part="score"><b>{`${l1.metCount} of ${l1.total}`}</b><span> requirements met</span></p>
      <VerdictLine m={m} />
      {slot}
      {l1.met && l1.lapse ? <p className="gp-cmmc-note" data-part="lapse">{`Re-affirm by ${fmtShort(l1.lapse.date)}`}</p> : null}
      <PrimaryActions m={m} />
      {after}
    </section>
  );
}

function BlockerList({ m }: { m: ScorecardModel }) {
  if (!m.blockers.length) {
    return <p className="gp-dim" data-part="no-blockers" id="cmmc-blockers-h" tabIndex={-1}>{m.l2 ? 'Nothing is blocking conditional status.' : 'Every Level 1 requirement is Met.'}</p>;
  }
  return (
    <section aria-labelledby="cmmc-blockers-h" className="gp-sec" data-part="cmmc-blockers">
      <h3 id="cmmc-blockers-h" className="gp-sec-h" tabIndex={-1}>{m.blockersHeading}</h3>
      <ol className="gp-cmmc-blockers">
        {m.blockers.map((b) => <BlockerItem key={b.key} b={b} />)}
      </ol>
    </section>
  );
}

function BlockerItem({ b }: { b: Blocker }) {
  return (
    <li className="gp-cmmc-blocker" data-blocker={b.key}>
      <div className="gp-cmmc-bhead">
        <h4 id={b.anchor} tabIndex={-1}>{b.title}</h4>
        {b.action ? <Link className="gp-linkbtn gp-linkbtn--compact" to={b.action.href} data-part="blocker-action">{b.action.label}</Link> : null}
      </div>
      {b.detail ? <p>{b.detail}</p> : null}
      {b.rows.length ? (
        <ul className="gp-cmmc-rows" aria-label={b.title}>
          {b.rows.map((r) => {
            const name = r.code ? `${r.title} · ${r.code}` : r.title;
            const inPage = r.href.startsWith('#');
            return (
              <li key={r.id} id={r.anchor} tabIndex={-1} data-tone={r.tone}>
                <span className="gp-cmmc-name">
                  {inPage
                    ? <a href={r.href} aria-label={name} onClick={(e) => { e.preventDefault(); focusId(r.href.slice(1)); }}>{r.title}</a>
                    : <Link to={r.href} aria-label={name}>{r.title}</Link>}
                  {r.code ? <span className="gp-dim gp-cmmc-code" aria-hidden="true">{r.code}</span> : null}
                  {r.tag ? <Chip text={r.tag} tone="outline" /> : null}
                </span>
                {r.meta ? <span className="gp-cmmc-meta">{r.meta}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The affirmation gate on the Audit tab (F4): the same gate as Prepare, no
 * link back to itself. Ready → asking the affirming official is the filled
 * primary (the signing flow itself lands with V102-8-6); the viewer never gets
 * a mailto to themselves.
 */
export function CmmcAuditGate({ slug, prefix, card, periodId, tabHref, viewerId = '', isAdmin = false }: {
  slug: string; prefix: string; card: ProgramCard; periodId: string; tabHref: CmmcScorecardProps['tabHref']; viewerId?: string; isAdmin?: boolean;
}) {
  const { d, model } = useScorecardModel(slug, prefix, card, periodId, tabHref);
  if (d.score.isLoading || d.gate.isLoading) return <Spinner label="Loading the affirmation gate" />;
  if (!model?.gate) return <Alert variant="error"><p>The affirmation gate could not load.</p><Button onClick={() => { void d.score.refetch(); void d.gate.refetch(); }}>Retry</Button></Alert>;
  const g: GateView = { ...model.gate, action: undefined, steps: model.gate.steps.filter((s) => s.step !== 'not_pinned') };
  const label = card.periods.find((p) => p.period_id === periodId)?.label ?? '';
  return (
    <GateCard gate={g}>
      {g.locked ? <p className="gp-cmmc-note"><Link to={tabHref('prepare')}>What’s left is on Prepare</Link></p> : null}
      {auditGateReady(model)
        ? (isAdmin
          // The affirming official is an owner / admin: when that is the viewer, say so instead of a mailto to themselves.
          ? <p className="gp-cmmc-note" role="status" data-part="you-sign">You sign this · signing opens here soon</p>
          : <div className="gp-actions" data-part="ask-sign"><AskAdmin slug={slug} verb="sign" what="signs the CMMC affirmation" subject={`GAC Pro: CMMC ${label} is ready to sign`} exclude={viewerId} primary /></div>)
        : null}
    </GateCard>
  );
}

/**
 * CMMC Audit (F4 / flow N3): the EXISTING period — never a form for a second
 * window — with Start fieldwork behind a confirm (it locks the tests), hidden
 * once the period is in fieldwork or the affirmation is signed; then the gate.
 * When the gate is ready, asking the signer is the filled primary and Start
 * fieldwork (if still shown) is secondary. Writes: start fieldwork — program
 * lead or owner/admin, server-enforced.
 */
export function CmmcAudit({ slug, prefix, card, periodId, tabHref, canManage, isAdmin, viewerId }: {
  slug: string; prefix: string; card: ProgramCard; periodId: string; tabHref: CmmcScorecardProps['tabHref']; canManage: boolean; isAdmin: boolean; viewerId: string;
}) {
  const { model } = useScorecardModel(slug, prefix, card, periodId, tabHref);
  const p = card.periods.find((x) => x.period_id === periodId)!;
  const signed = !!model?.gate?.line.startsWith('Signed');
  const inFieldwork = p.phase === 'fieldwork';
  return <>
    <ul className="gp-gap-list" aria-label="Assessment window">
      <li className="gp-gap" data-part="cmmc-period">
        <span><strong>{p.label}</strong>{/* flow R3-4: years on both ends (a window crosses a year). */}{p.window ? ` · ${fmtProgramDate(p.window.start)} – ${fmtProgramDate(p.window.end)}` : ''}</span>
        {inFieldwork || signed
          ? <span className="gp-cmmc-note">{signed ? 'Signed · tests locked' : 'Fieldwork · tests locked'}</span>
          : canManage
            ? <CmmcFieldworkButton slug={slug} program={card.program} periodId={periodId} label={p.label} />
            : <AskAdmin slug={slug} compact verb="start fieldwork" what="runs the program" subject={`GAC Pro: start fieldwork for ${p.label}`} exclude={viewerId} />}
      </li>
    </ul>
    {!inFieldwork && !signed ? <p className="gp-cmmc-note">Starting fieldwork locks the tests the self-assessment is judged by.</p> : null}
    <CmmcAuditGate slug={slug} prefix={prefix} card={card} periodId={periodId} tabHref={tabHref} viewerId={viewerId} isAdmin={isAdmin} />
  </>;
}

function CmmcFieldworkButton({ slug, program, periodId, label }: { slug: string; program: string; periodId: string; label: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      await startProgramFieldwork(slug, program, periodId);
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      setOpen(false);
    } catch (e) { setError(e instanceof Error ? e.message : 'Fieldwork could not start.'); return false; }
    finally { setBusy(false); }
  };
  return <>
    <Button variant="secondary" onClick={() => setOpen(true)} aria-label={`Start fieldwork for ${label}`}>Start fieldwork</Button>
    <ActionDialog open={open} title={`Start fieldwork for ${label}?`} submitLabel="Start fieldwork" onClose={() => setOpen(false)} onSubmit={go} busy={busy} error={error}>
      <p data-part="fieldwork-confirm">This locks the tests the self-assessment is judged by. You can’t change them after.</p>
    </ActionDialog>
  </>;
}

/** The Audit tab's ready state (also decides which Audit action is the filled primary). */
export const auditGateReady = (m: ScorecardModel | null) => !!m?.gate && !m.gate.locked && m.gate.line.startsWith('Ready');

export function GateCard({ gate, children, renderStep }: { gate: GateView; children?: ReactNode; renderStep?: (step: GateView['steps'][number]['step']) => ReactNode }) {
  return (
    <section className="gp-cmmc-gate" aria-labelledby="cmmc-gate-h" data-part="cmmc-gate" data-locked={gate.locked || undefined}>
      <h3 id="cmmc-gate-h" tabIndex={-1}>{gate.title}{gate.locked ? <Chip text="Locked" tone="lock" /> : null}</h3>
      <p data-part="gate-line">{gate.line}</p>
      {gate.steps.length ? (
        <ul className="gp-cmmc-steps" aria-label="Still to do before signing">
          {gate.steps.map((s) => (
            <li key={s.step} data-step={s.step}>
              <span className="gp-cmmc-step-head">
                <span>{s.text}</span>
                {s.action ? <Link className="gp-linkbtn gp-linkbtn--compact" to={s.action.href}>{s.action.label}</Link> : null}
              </span>
              {renderStep?.(s.step) ?? null}
            </li>
          ))}
        </ul>
      ) : null}
      {children}
      {gate.action ? <div className="gp-actions"><Link className="gp-linkbtn" to={gate.action.href}>{gate.action.label}</Link></div> : null}
    </section>
  );
}


const KIND_NAME: Record<string, string> = {
  check: 'Check', artifact: 'Document', document: 'Linked document', interview: 'Interview record', sample: 'Sample',
  population: 'Population', reconciliation: 'Reconciliation run', attestation: 'Attestation document',
};

/**
 * F3 / T8R2-2: the results awaiting review, inline inside their gate step (who
 * proposed what, when, why, and the cited evidence). Mark reviewed shows for a
 * program lead / owner / admin who didn't propose it; the server enforces both
 * rules (403 / 403 `sod`) whatever the UI shows.
 * V102-8-5: "Send back…" asks what should change (required); the recorder sees
 * it on the cell with Revise.
 */
function ReviewList({ slug, prefix, program, periodId, canManage, viewerId, names, titles, announce }: {
  slug: string; prefix: string; program: string; periodId: string; canManage: boolean; viewerId: string;
  names?: ReadonlyMap<string, string>; titles: ReadonlyMap<string, string>; announce: (s: string) => void;
}) {
  const q = useQuery({ queryKey: queryKeys.determinations(slug, program, periodId, 'proposed'), queryFn: () => listDeterminations(slug, program, periodId, 'proposed'), retry: false, staleTime: 30_000 });
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ id: string; text: string } | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const rows = q.data?.determinations ?? [];
  // N5: "Review the result" (Gaps / Requirements) lands on #review-<id>: once the list loads, bring that item into view and focus its heading.
  const { hash } = useLocation();
  const loaded = !!q.data;
  useEffect(() => {
    if (!loaded || !hash.startsWith('#review-')) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!el) return;
    el.scrollIntoView?.({ block: 'center' });
    (el.querySelector<HTMLElement>('[data-part="review-h"]') ?? el).focus();
  }, [loaded, hash]);
  const review = async (id: string, objective: string) => {
    if (busy) return;
    setBusy(id); setMsg(null);
    const i = rows.findIndex((r) => r.determination_id === id);
    const next = rows.slice(i + 1).concat(rows.slice(0, i)).find((r) => r.proposed_by !== viewerId);
    try {
      await reviewDetermination(slug, program, periodId, id);
      announce(`Reviewed · ${objective} · ${rows.length - 1} left`);
      // Focus the next row's button, else the gate heading (the list re-renders after the refetch).
      requestAnimationFrame(() => focusId(next ? `review-btn-${next.determination_id}` : 'cmmc-gate-h'));
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
    } catch (e) {
      const code = e instanceof ApiError ? (e.body as { code?: string } | undefined)?.code : undefined;
      setMsg({ id, text: code === 'sod' ? 'You prepared part of this result · another reviewer is needed.' : e instanceof Error ? e.message : 'The review could not be saved.' });
    } finally { setBusy(null); }
  };
  if (q.isLoading) return <Spinner label="Loading results awaiting review" />;
  if (q.error) return <Alert variant="error"><p>Results awaiting review could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  if (!rows.length) return null;
  return (
    <ul className="gp-cmmc-rows" id="cmmc-review" tabIndex={-1} aria-label="Results awaiting review" data-part="review-list">
      {rows.map((d) => {
        const reqId = d.objective_id.replace(/\[[^\]]*\]$/, '');
        const objective = d.objective_id.slice(d.objective_id.lastIndexOf(':') + 1);
        const who = names?.get(d.proposed_by) ?? 'a colleague';
        const mine = d.proposed_by === viewerId;
        // R3-3: the server names each cited ref inside this workspace (title null = not found here).
        const refs = (d as typeof d & { evidence?: ResolvedEvidenceRef[] }).evidence ?? [];
        return (
          <li key={d.determination_id} id={`review-${d.determination_id}`} data-determination={d.determination_id}>
            <h4 className="gp-cmmc-name gp-cmmc-reviewh" tabIndex={-1} data-part="review-h"><span>{titles.get(reqId) ?? reqRef(reqId)}</span><span className="gp-dim gp-cmmc-code">{objective}</span></h4>
            <span className="gp-cmmc-meta">{`Proposed ${d.result === 'met' ? 'Met' : d.result === 'not_met' ? 'Not met' : 'N/A'} by ${mine ? 'you' : who} · ${fmtShort(d.proposed_at)}`}</span>
            {d.rationale ? <span className="gp-cmmc-why">{d.rationale}</span> : null}
            {/* R4: one cited item per line; a link only where the Evidence page lists that row; a check shows its status inline. */}
            {refs.length ? (
              <ul className="gp-cmmc-cited" aria-label={`Evidence cited for ${objective}`} data-part="cited">
                {refs.map((r) => (
                  <li key={`${r.kind}:${r.id}`} data-ref={r.kind}>
                    {r.title === null
                      ? <>{`${KIND_NAME[r.kind] ?? 'Evidence'} not found · `}<AskAdmin slug={slug} compact verb="re-cite it" what="recorded this result" subject={`GAC Pro: evidence for ${objective} not found`}
                          roles={['owner', 'admin', 'editor']} prefer={d.proposed_by} exclude={viewerId} lead="The recorder" /></>
                      : r.href ? <Link to={`${prefix}${r.href}`}>{r.title}</Link>
                        : <span>{[r.title, r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1).replace(/_/g, ' ') : null, r.observed_at ? fmtShort(r.observed_at) : null].filter(Boolean).join(' · ')}</span>}
                  </li>
                ))}
              </ul>
            ) : <span className="gp-cmmc-why" data-part="cited">No evidence cited</span>}
            {canManage && !mine && sending === d.determination_id ? (
              <SendBackForm slug={slug} program={program} periodId={periodId} row={d} to={who}
                onCancel={() => { setSending(null); requestAnimationFrame(() => focusId(`review-sb-${d.determination_id}`)); }}
                onSent={() => {
                  setSending(null);
                  announce(`Sent back to ${who} · ${objective}`);
                  focusAfterSettle(() => document.getElementById('cmmc-gate-h'));
                  void Promise.all(invalidationMap['determination:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
                }} />
            ) : canManage && !mine
              ? <span className="gp-actions">
                  <button type="button" id={`review-btn-${d.determination_id}`} className="gp-linkbtn gp-linkbtn--compact" aria-disabled={busy === d.determination_id || undefined}
                    onClick={() => void review(d.determination_id, objective)} aria-label={`Mark reviewed · ${objective}`}>{busy === d.determination_id ? 'Saving…' : 'Mark reviewed'}</button>
                  <button type="button" id={`review-sb-${d.determination_id}`} className="gp-cmmc-quiet" onClick={() => setSending(d.determination_id)}
                    aria-label={`Send back · ${objective}`} data-part="review-send-back">Send back…</button>
                </span>
              : mine ? <span className="gp-cmmc-meta">You proposed this · another reviewer is needed</span> : null}
            {msg?.id === d.determination_id ? <span role="alert" className="gp-field-err">{msg.text}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}
